import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Printer, ImageOff } from 'lucide-react';

// The user manual. It is kept in the app rather than in a document nobody can
// find, and it is written from what the screens actually do — when a screen
// changes, this page changes with it.
//
// Screenshots are pictures of the real screens, so they carry real customers
// and real figures. They are fetched from the API behind the sign-in rather
// than from the public web root, where anyone guessing a file name could read
// them. `scripts/manual_screenshots.py` takes them; a section with no picture
// yet says so instead of showing a broken image.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  {
    id: 'getting-started',
    group: 'Start here',
    title: 'Signing in',
    purpose: 'Every staff member signs in with an email and password, then a one-time code (OTP) sent by SMS or email.',
    steps: [
      'Enter your email and password, then press Sign In.',
      'A 6-digit code is sent to you. Type it in to finish signing in.',
      'A code is valid for 10 minutes. If it does not arrive, press Resend Code.',
      'Forgot your password? Use Forgot Password — a reset code is sent the same way.',
    ],
    troubleshoot: [
      {
        problem: 'The OTP never arrives',
        fix: 'Wait a full minute, then Resend. If the SMS still does not come, the phone number on your staff record may be wrong or out of network reach — ask an admin to check it under Staff Management. Admins can also confirm whether the code was issued at all.',
      },
      {
        problem: '"Invalid OTP"',
        fix: 'The code expired (10 minutes) or a newer code was sent. Only the most recent code works — press Resend and use the newest one.',
      },
    ],
  },
  {
    id: 'dashboard',
    group: 'Start here',
    title: 'Dashboard',
    purpose: 'The opening screen: today\'s trading position, stock warnings and what is waiting for you.',
    steps: [
      'Figures are for the whole company, not just your own work.',
      'The bell at the top right carries notifications — approvals waiting, new portal orders, low stock, scans.',
    ],
    troubleshoot: [
      {
        problem: 'A number looks wrong',
        fix: 'Every figure comes from a register you can open yourself — sales from Invoices, stock from Finished Goods and Raw Materials. Open the register and compare; if they disagree, the register is right and the difference is worth reporting.',
      },
    ],
  },
  {
    id: 'customers',
    group: 'Selling',
    title: 'Customers',
    purpose: 'The customer register — everyone you sell to, whether they were onboarded by staff or signed up on the website.',
    steps: [
      'Add Customer records a buyer as either an individual or a company.',
      'For a company, the customer name IS the company name and Contact Person is the person you deal with. For an individual, the name is that person, and Company is only the organisation they buy for.',
      'TIN matters — it is what a company invoice must carry.',
      'Clicking a customer opens their profile: their orders, invoices and deliveries in one place, each printable from there.',
      'Which fields are compulsory at onboarding is set in Settings → Customer Onboarding.',
    ],
    troubleshoot: [
      {
        problem: 'An invoice prints "Company: N/A" or repeats the name twice',
        fix: 'The customer was saved with the wrong type. Open the customer and check Customer Type — company details print differently from an individual\'s.',
      },
      {
        problem: 'I cannot see a colleague\'s customers',
        fix: 'That is by design. Only roles carrying the "view ALL customers" privilege see the whole register; everyone else sees their own.',
      },
    ],
  },
  {
    id: 'orders',
    group: 'Selling',
    title: 'Orders and Customer Orders',
    purpose: 'Orders are raised by staff. Customer Orders are the ones buyers place themselves on the website.',
    steps: [
      'A portal order arrives as Pending and is worked through Approved → Shipped → Delivered, or Rejected.',
      'Stock is deducted once, when the order is approved — approving twice never deducts twice.',
      'The customer picks a payment account at checkout, and the account they paid into is stored with the order.',
      'An order confirmation can be printed for the customer from the order.',
    ],
    troubleshoot: [
      {
        problem: '"Insufficient stock" on approval',
        fix: 'The product\'s stock in Products Management is below the order quantity. Book the produced cartons in Finished Goods — that raises the product\'s stock — then approve again.',
      },
      {
        problem: 'A customer says they paid but the order shows unpaid',
        fix: 'Payment is marked by finance against the payment reference the customer quoted. Check Bank Transactions and the reference on the order.',
      },
    ],
  },
  {
    id: 'products',
    group: 'Selling',
    title: 'Products Catalog',
    purpose: 'The products you make and sell, their stock in cartons, their standard prices, and what packing one carton pays.',
    steps: [
      'Stock Quantity is counted in cartons. You rarely type it by hand — booking a batch in Finished Goods raises it, and selling or scanning a carton lowers it.',
      'Retail and Wholesale Price here are the fallback figures. Once a product has an active price record in Pricing, that record decides the figure charged.',
      'Packing Rate is what packing one carton of this product pays a packer. Packing Records uses it.',
      'Featured controls whether the product appears on the public welcome page.',
    ],
    troubleshoot: [
      {
        problem: 'I changed the price here but customers still see the old one',
        fix: 'A product with an active price record is priced from that record, not from this screen. Change it in Pricing.',
      },
      {
        problem: 'Stock here does not match the store',
        fix: 'Compare with Finished Goods — the batches booked there are the cartons this figure counts. A batch produced but never booked is the usual cause.',
      },
    ],
  },
  {
    id: 'pricing',
    group: 'Selling',
    title: 'Pricing',
    purpose: 'What each product actually costs a customer, including quantity (MOQ) bands for wholesale buyers.',
    steps: [
      'Manage opens a product\'s price history. History is never deleted.',
      'New Price creates a price list: one retail price, plus as many wholesale bands as you need — each band is a minimum quantity and the unit price at that quantity.',
      'An order takes the highest band its quantity reaches: with bands 100+, 500+ and 1,000+, an order of 600 is priced at the 500+ figure.',
      'To put several saved prices in force together, tick them in the history and press "Activate selected" — their bands merge into one live price list. Where two ticked prices name the same minimum quantity, the newer figure wins; the retail price comes from the newest one ticked.',
      'Suspending the active price makes the product fall back to the retail and wholesale figures on the product itself.',
      'VIP customers are priced from the VIP figure on their customer record, ahead of everything here.',
    ],
    troubleshoot: [
      {
        problem: 'Only one of my MOQ prices seems to apply',
        fix: 'Each price record is a whole price list, so activating one suspends the last. Either put every band inside one record with + Add tier, or tick the records you want and use "Activate selected" to merge them.',
      },
      {
        problem: 'My bands disappeared after a price change',
        fix: 'Fixed — the new-price form now opens with the bands already in force. Older records are still in the history and can be re-activated.',
      },
      {
        problem: 'A retail customer is not getting the band price',
        fix: 'Bands apply to wholesale buyers. Retail customers pay the single retail price.',
      },
    ],
  },
  {
    id: 'raw-materials',
    group: 'Making',
    title: 'Raw Materials',
    purpose: 'What is in the material store, and the requests production makes against it.',
    steps: [
      'The Inventory tab is the store: quantity, unit and reorder level per material. Bags are also shown in kilos (1 bag = the kg figure on screen).',
      'Request Material for Production asks for a quantity, the Production Date, and the purpose.',
      'You do not invent a batch number. Choosing the production date writes the batch code for that day — CK-DDMMYY — which is the company code from QP-TR-01.',
      'A request goes to finance or the inventory coordinator for approval. Stock is only deducted when the request is approved.',
      'That batch is now open, and QR Codes, Finished Goods and Packing Records all offer it in their batch lists.',
    ],
    troubleshoot: [
      {
        problem: 'My batch does not appear when generating QR codes',
        fix: 'A batch is created by a material usage request. If there is no request for that day\'s production, raise one — or type the batch number by hand on the QR screen.',
      },
      {
        problem: 'Stock did not go down after I requested materials',
        fix: 'Correct — a pending request reserves nothing. Stock moves when the request is approved.',
      },
      {
        problem: 'Two production runs on the same day',
        fix: 'They share one batch code, because the code is the date. Use the purpose and notes to tell the runs apart, or adjust the batch number by hand on the finished-goods row.',
      },
    ],
  },
  {
    id: 'qr-codes',
    group: 'Making',
    title: 'QR Codes and Barcodes',
    purpose: 'Coding a produced batch: one QR code for the batch and a unique one for every carton in it.',
    steps: [
      'Generate QR Code: pick the product, then pick the production batch from the list (it fills in the production date). The Auto button writes CK-DDMMYY from the date if the batch is not on the list.',
      'Quantity is the number of cartons. Each carton gets its own code, numbered YYYYMMDD-C0001 upward.',
      'View Cartons lists them, and prints the labels for the whole batch in one go.',
      'The cartons coded here are booked onto Finished Goods and added to the product\'s stock automatically — you never enter the batch a second time.',
      'Scanning a carton at sale records the scan and takes one carton off stock. A carton already tied to an invoice is marked delivered instead.',
    ],
    troubleshoot: [
      {
        problem: 'I generated the batch twice',
        fix: 'The finished-goods register books a batch once, so the cartons are not counted twice. The duplicate QR batch itself can be left — the batch trace under Quality shows what belongs to it.',
      },
      {
        problem: 'Labels will not print',
        fix: 'The print window is a popup. Allow popups for this site in your browser, then press print again.',
      },
      {
        problem: '"This batch is fully sold out"',
        fix: 'Every carton in the batch has been scanned or sold. Generate the next batch rather than re-scanning this one.',
      },
    ],
  },
  {
    id: 'finished-goods',
    group: 'Making',
    title: 'Finished Goods',
    purpose: 'The carton register: what each batch put on the shelf, for any product — not chalk alone.',
    steps: [
      'Most rows arrive by themselves, booked when QR codes were generated for the batch. They carry a small QR mark.',
      'Add Carton Batch is for cartons that were not coded: pick the batch (or let the production date write it), choose the product, and enter Cartons Produced.',
      'Cartons Produced is the count that matters. Saving a row adds those cartons to the product\'s stock in Products Management; correcting a row moves the stock by the difference.',
      'Quality Status marks a batch approved, pending or rejected.',
    ],
    notes: [
      'The old "Cartons/Batch" box has been removed. It counted nothing the system read — Cartons Produced was always the real figure.',
    ],
    troubleshoot: [
      {
        problem: '"Batch is already on the register"',
        fix: 'That batch and product are booked. Edit that row instead of adding a second one — editing keeps the product\'s stock in step.',
      },
      {
        problem: 'Stock went up twice for one batch',
        fix: 'That happens only if the same cartons were entered under two different batch numbers. Delete or correct one of the rows; the stock follows the correction.',
      },
    ],
  },
  {
    id: 'packing',
    group: 'Making',
    title: 'Packing Records',
    purpose: 'Cartons packed by each worker, and what that comes to — packing is paid by the carton.',
    steps: [
      'Record Packing: the packer\'s name, the product, the batch, how many cartons and the date.',
      'Leave the rate at 0 and the product\'s Packing Rate is used. The rate actually used is written onto the record, so changing a product\'s rate later never rewrites what a packer was already owed.',
      'Set each product\'s rate in Products Catalog → Packing Rate.',
      'Per Worker totals the period for each packer. Print pay sheet gives you the sheet to pay from, with a signature column.',
      'Packers do not need a login — the name is enough. Names already used are offered as you type, so the same person is not spelt three ways.',
    ],
    troubleshoot: [
      {
        problem: '"has no packing rate yet"',
        fix: 'Set the Packing Rate on that product, or type the rate for this one record.',
      },
      {
        problem: 'A worker appears twice in the totals',
        fix: 'Their name was typed two different ways. Edit the entries to one spelling — the suggestions list helps next time.',
      },
      {
        problem: 'I cannot see this screen',
        fix: 'It needs the Production privilege. Admins grant it under Staff Management → Roles & Privileges.',
      },
    ],
  },
  {
    id: 'quality',
    group: 'Making',
    title: 'Quality (TBS)',
    purpose: 'The quality file the standards bureau expects: complaints, recalls, machine maintenance, calibration, rejected material and corrective actions.',
    steps: [
      'Each tab is its own register, and each entry is dated and attributed.',
      'Batch Trace is the important one: type a batch number and it shows the QR batch, its cartons, the deliveries it went out on, and any complaints, recalls or rejected material against it.',
    ],
    troubleshoot: [
      {
        problem: 'Batch Trace finds nothing',
        fix: 'Check the batch number is the code the system used — CK-DDMMYY. The batch lists on the QR and Finished Goods screens show the exact spelling.',
      },
    ],
  },
  {
    id: 'invoices',
    group: 'Paperwork',
    title: 'Invoices and Proformas',
    purpose: 'Official invoices and proformas, and the paper the customer receives.',
    steps: [
      'A proforma is a quotation. Finance validates it into an official invoice.',
      'The customer\'s details are copied onto the invoice when it is raised, so a later change to their record never rewrites paperwork already issued.',
      'Tax comes from the tax register in Settings. An invoice carrying no tax prints no tax line at all.',
      'Payment details and the Lipa Namba QR come from the payment accounts in Settings; older invoices show the accounts in force today so the customer still knows where to pay.',
      'Payment terms default from Settings → Invoice Defaults and can be overridden per invoice.',
    ],
    troubleshoot: [
      {
        problem: 'Nothing happens when I press Print',
        fix: 'The document opens in a popup window. Allow popups for this site, then print again.',
      },
      {
        problem: 'The wrong bank account is printed',
        fix: 'Set which accounts are default in Settings → Payment Accounts. Invoices already raised keep the accounts they were raised with.',
      },
      {
        problem: 'The QR on the invoice does not scan',
        fix: 'Only a Lipa Namba (mobile money) carries a scannable code. Bank accounts are typed into a banking app, so no code is printed for them.',
      },
    ],
  },
  {
    id: 'delivery-notes',
    group: 'Paperwork',
    title: 'Delivery Notes',
    purpose: 'What left the yard, with whom, and who signed for it.',
    steps: [
      'A note carries the delivery address, date, vehicle and driver, and prints with lines for the receiver\'s name, signature and date.',
      'Batch numbers on the note are what makes a recall traceable — Quality → Batch Trace reads them.',
    ],
    troubleshoot: [
      {
        problem: 'The delivery address is blank',
        fix: 'The note falls back to the customer\'s address when no delivery address was entered. Fill one in on the note, or on the customer record.',
      },
    ],
  },
  {
    id: 'finance',
    group: 'Money',
    title: 'Finance',
    purpose: 'Expenses, other income, purchases, assets, loans — and the profit and loss they add up to.',
    steps: [
      'Each tab is a register you add entries to; Profit & Loss reads them together with sales.',
      'Overview is the summary for the period on screen.',
    ],
    troubleshoot: [
      {
        problem: 'Profit looks too high',
        fix: 'Expenses that were never entered cannot be deducted. Check the Expenses and Purchases tabs cover the whole period you are looking at.',
      },
    ],
  },
  {
    id: 'bank',
    group: 'Money',
    title: 'Bank Details',
    purpose: 'Bank transactions recorded by hand, for reconciling what customers say they paid.',
    steps: [
      'Record the transaction with the reference the customer quoted — that reference is what ties a payment to an invoice.',
    ],
  },
  {
    id: 'sales-targets',
    group: 'Money',
    title: 'Sales Targets',
    purpose: 'A target amount per staff member over a period, and what they have achieved against it.',
    steps: [
      'Set the target, the period and its start and end dates. Achievement is measured against sales in that window.',
    ],
  },
  {
    id: 'reports',
    group: 'Money',
    title: 'Reports',
    purpose: 'Sales, raw materials and carton stock, on screen and as CSV exports you can open in Excel.',
    steps: [
      'Choose the period, then export. The CSV carries the same figures as the screen.',
    ],
    troubleshoot: [
      {
        problem: 'The export is empty',
        fix: 'Nothing falls inside the dates chosen. Widen the period.',
      },
    ],
  },
  {
    id: 'staff',
    group: 'Administration',
    title: 'Staff, Roles and Privileges',
    purpose: 'Who can sign in, and what each of them is allowed to do.',
    steps: [
      'Staff lists the accounts. Roles & Privileges is where a role is given its powers.',
      'Admin holds every privilege. Every other role — built-in or one you create — is a named set of privileges.',
      'Reading a shared register is open to signed-in staff; privileges gate the actions: creating, approving, suspending.',
      'A menu item a person cannot see is a privilege they were not given.',
      'Some privileges to know: Production (packing records), Finished Goods (carton register), Raw Materials approvals, Pricing, and Customers — view ALL.',
    ],
    troubleshoot: [
      {
        problem: 'A colleague cannot see a screen you can',
        fix: 'Grant their role the matching privilege here. They must sign out and back in for a change to reach their menu.',
      },
      {
        problem: '"Missing privilege" when saving',
        fix: 'Reading was allowed, acting was not. The message names the privilege that is missing.',
      },
    ],
  },
  {
    id: 'website',
    group: 'Administration',
    title: 'Website',
    purpose: 'What visitors see on the public welcome page — the slider and the featured products.',
    steps: [
      'Only featured products appear publicly. Turn the toggle off on a product to hide it; sort order sets the order they appear in.',
      'The public page never shows stock figures.',
    ],
  },
  {
    id: 'settings',
    group: 'Administration',
    title: 'Settings',
    purpose: 'The registers the rest of the system reads: tax, payment accounts, invoice defaults, and what customers must supply at onboarding.',
    steps: [
      'Tax Configuration holds the tax name and rate invoices use.',
      'Payment Accounts holds the bank accounts and Lipa Nambas printed on invoices. Mark the ones in force as default.',
      'Invoice Defaults sets the payment terms every new invoice starts with.',
      'Customer Onboarding — Required Fields decides which details cannot be left blank when a customer is registered.',
    ],
    troubleshoot: [
      {
        problem: 'A change here did not reach an old invoice',
        fix: 'That is deliberate. Paperwork already issued keeps the figures it was issued with.',
      },
    ],
  },
  {
    id: 'notifications',
    group: 'Administration',
    title: 'Notifications',
    purpose: 'The bell: approvals waiting, new portal orders, invoices, deliveries, scans and low stock.',
    steps: [
      'Notifications go to the roles that can act on them, never to the person who caused them.',
      'The tabs filter by kind; unread are counted on the bell.',
    ],
  },
  {
    id: 'audit-logs',
    group: 'Administration',
    title: 'Audit Logs',
    purpose: 'Who did what, and when. This is where a disputed change is settled.',
    steps: [
      'Price changes, approvals, packing records, customer changes and more are recorded with the person and the time.',
    ],
  },
];

const COMMON = [
  {
    problem: 'A print window does not open',
    fix: 'Every printed document opens in a popup. Allow popups for this site in the browser, then press print again.',
  },
  {
    problem: 'A screen is blank or an action fails silently',
    fix: 'Your session may have expired. Sign out and sign in again. If it persists, note the exact screen and the time and report it — the audit log and server log can be read for that moment.',
  },
  {
    problem: 'I changed something and cannot see it',
    fix: 'Refresh the page. Menu and privilege changes need a fresh sign-in.',
  },
  {
    problem: 'Figures disagree between two screens',
    fix: 'Registers are the source: Finished Goods for cartons, Raw Materials for the store, Invoices for sales. A dashboard tile is a summary of them, never a separate number.',
  },
];

function Screenshot({ id, title }) {
  const [src, setSrc] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/manual-screenshots/${id}.png`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setMissing(true);
      }
    };
    load();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);

  if (missing) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500 print:hidden">
        <ImageOff size={14} />
        No screenshot for this section yet.
      </div>
    );
  }
  if (!src) {
    return <div className="h-32 rounded-md border border-slate-200 bg-slate-50 animate-pulse print:hidden" />;
  }
  return <img src={src} alt={`${title} screen`} className="rounded-md border border-slate-200 w-full" />;
}

export default function Manual() {
  const [query, setQuery] = useState('');

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    const hit = (text) => (text || '').toLowerCase().includes(q);
    return SECTIONS.filter((section) =>
      hit(section.title) || hit(section.purpose) ||
      (section.steps || []).some(hit) ||
      (section.notes || []).some(hit) ||
      (section.troubleshoot || []).some((t) => hit(t.problem) || hit(t.fix))
    );
  }, [query]);

  const groups = useMemo(() => {
    const order = [];
    const byGroup = {};
    for (const section of sections) {
      if (!byGroup[section.group]) { byGroup[section.group] = []; order.push(section.group); }
      byGroup[section.group].push(section);
    }
    return order.map((name) => ({ name, sections: byGroup[name] }));
  }, [sections]);

  return (
    <div data-testid="manual-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">User Manual</h1>
          <p className="text-slate-600 mt-1">
            What every screen is for, how to work it, and what to do when something looks wrong
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="print:hidden">
          <Printer size={16} className="mr-2" /> Print manual
        </Button>
      </div>

      <div className="relative mb-6 max-w-md print:hidden">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Search the manual — e.g. batch, OTP, MOQ, popup"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="manual-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <nav className="hidden lg:block sticky top-6 self-start space-y-4 print:hidden">
          {groups.map((group) => (
            <div key={group.name}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{group.name}</p>
              <ul className="space-y-1">
                {group.sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="text-sm text-slate-600 hover:text-[#8FC043]">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Help</p>
            <a href="#common" className="text-sm text-slate-600 hover:text-[#8FC043]">If something goes wrong</a>
          </div>
        </nav>

        <div className="space-y-6">
          {sections.length === 0 && (
            <p className="text-slate-500">Nothing in the manual matches “{query}”.</p>
          )}

          {sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-6 bg-white rounded-lg border border-slate-200 p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-slate-900">{section.title}</h2>
                <Badge variant="outline" className="text-slate-500">{section.group}</Badge>
              </div>
              <p className="text-slate-600 mb-4">{section.purpose}</p>

              <div className="mb-4">
                <Screenshot id={section.id} title={section.title} />
              </div>

              <h3 className="text-sm font-semibold text-slate-700 mb-2">How it works</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mb-4">
                {section.steps.map((step, i) => <li key={i}>{step}</li>)}
              </ul>

              {section.notes && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 mb-4">
                  {section.notes.map((note, i) => <p key={i}>{note}</p>)}
                </div>
              )}

              {section.troubleshoot && (
                <>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">If something looks wrong</h3>
                  <dl className="space-y-2 text-sm">
                    {section.troubleshoot.map((item, i) => (
                      <div key={i} className="rounded-md border border-amber-200 bg-amber-50 p-3">
                        <dt className="font-semibold text-amber-900">{item.problem}</dt>
                        <dd className="text-amber-900/80 mt-1">{item.fix}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </section>
          ))}

          <section id="common" className="scroll-mt-6 bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-2">If something goes wrong anywhere</h2>
            <dl className="space-y-2 text-sm">
              {COMMON.map((item, i) => (
                <div key={i} className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <dt className="font-semibold text-amber-900">{item.problem}</dt>
                  <dd className="text-amber-900/80 mt-1">{item.fix}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>
    </div>
  );
}
